import type { Editor } from "@tiptap/core";
import { mergeAttributes, Node } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

import { inlineMeasureAttributes } from "./use-inline-measure";

export interface InlineMeasureClickData {
  measureId: string;
  measureDataSettings: string;
  content: string;
  element: HTMLElement;
  editor: Editor;
}

export interface InlineMeasureOptions {
  HTMLAttributes?: Record<string, string | number | boolean>;
  /** Called when user clicks on an inline measure. Receives extracted data from attributes (and element for positioning). */
  onClick?: (data: InlineMeasureClickData) => void;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    inlineMeasure: {
      setInlineMeasure: (attrs: { measureId: string; measureDataSettings: string; content: string }) => ReturnType;
    };
  }
}

export const InlineMeasureExtension = Node.create<InlineMeasureOptions>({
  name: "inlineMeasure",

  addOptions() {
    return {
      HTMLAttributes: {},
      onClick: undefined
    };
  },

  group: "inline",

  inline: true,

  atom: true,

  addAttributes() {
    return {
      measureId: {
        default: null,
        parseHTML: (element) => element.getAttribute(inlineMeasureAttributes.measureId),
        renderHTML: (attrs) => (attrs.measureId ? { [inlineMeasureAttributes.measureId]: attrs.measureId } : {})
      },
      measureDataSettings: {
        default: null,
        parseHTML: (element) => element.getAttribute(inlineMeasureAttributes.measureDataSettings),
        renderHTML: (attrs) =>
          attrs.measureDataSettings ? { [inlineMeasureAttributes.measureDataSettings]: attrs.measureDataSettings } : {}
      },
      content: {
        default: "",
        parseHTML: (element) => element.textContent,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        renderHTML: (attrs) => ({})
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: `span.inline-measure[${inlineMeasureAttributes.measureId}]`,
        getAttrs: (dom) => {
          const el = dom as HTMLElement;
          return {
            measureId: el.getAttribute(inlineMeasureAttributes.measureId),
            measureDataSettings: el.getAttribute(inlineMeasureAttributes.measureDataSettings),
            content: el.textContent || ""
          };
        }
      }
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(
        { class: "inline-measure", contenteditable: "false" },
        this.options.HTMLAttributes ?? {},
        HTMLAttributes,
        // Override dir so it inherits from parent - prevents LTR "island" when paragraph is RTL
        { dir: "inherit" }
      ),
      node.attrs.content || ""
    ];
  },

  addCommands() {
    return {
      setInlineMeasure:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs
          })
    };
  },

  addProseMirrorPlugins() {
    const onClick = this.options.onClick;
    if (!onClick) return [];

    return [
      new Plugin({
        key: new PluginKey("inlineMeasureClick"),
        props: {
          handleClick: (view, _pos, event) => {
            if (event.button !== 0) return false;

            const target = event.target as HTMLElement | null;
            if (!target) return false;

            const measureEl = target.closest?.(
              `span.inline-measure[${inlineMeasureAttributes.measureId}]`
            ) as HTMLElement | null;

            if (measureEl && view.dom.contains(measureEl)) {
              const measureId = measureEl.getAttribute(inlineMeasureAttributes.measureId);
              const measureDataSettings = measureEl.getAttribute(inlineMeasureAttributes.measureDataSettings);
              const content = measureEl.textContent || "";

              if (measureId && measureDataSettings) {
                onClick({ measureId, measureDataSettings, content, element: measureEl, editor: this.editor });
                return true;
              }
            }
            return false;
          }
        }
      })
    ];
  }
});
